import React from 'react'
import { Link } from '@inertiajs/react'
import { cn } from '@/lib/utils'
import { currencySymbol, formatRateSym, nightsUntil } from '@/lib/calendar-utils'
import { BedDouble, Shield, Ban, DoorClosed, Moon, CalendarX, AlertTriangle, Link2 } from 'lucide-react'
import {
  computeRunningTotals,
  engineComputedTotal,
  stageLabel,
  effectColor,
  linkedOffsetLabel,
  overridePct,
  overridePctLabel,
  sameRoomAdjustment,
  sameRoomEngineLabel,
  sameRoomEngineRate,
} from '@/lib/pipeline-utils'
import { dormantReasonLabel } from '@/lib/target-utils'
import { nightsAway, formatPickupRooms, minNights, maxNights, ABBREV_TOOLTIPS } from '@/lib/plain-language'
import AdjustmentLabel from './AdjustmentContext'
import { useFigureScope } from '@/lib/figure-scope'
import type { Adjustment } from '@/types/calendar'

interface ComputationDetails {
  adjustments?: Adjustment[]
  min_rate?: number
  max_rate?: number
  [key: string]: unknown
}

interface RateData {
  rateAmount: number
  source: string
  stopSell: boolean
  closedToArrival: boolean
  closedToDeparture: boolean
  minStayArrival: number | null
  maxStay: number | null
  computationDetails: ComputationDetails
  pushedRateAmount: number | null
  syncStatus: 'synced' | 'pending' | 'never_pushed'
  /**
   * Combined inventory, on a room the building also sells on other listings: one server sentence per
   * listing, "Also sold on Wharf House Wing at £138 (+15%)". Absent everywhere else.
   */
  alsoSoldOn?: string[] | null
}

interface OccupancyData {
  booked: number
  available: number
  occupancyPct: number
  pickup7d: number | null
  /** Booked-rooms pickup over the trailing 7 days — the operator-facing pickup unit. */
  pickup7dRooms?: number | null
}

interface PropertyOccupancyData {
  booked: number
  available: number
  occupancyPct: number
  /** What the pooled figure is called: "Building" at a combined inventory (server); absent means "Hotel". */
  label?: string | null
}

export interface PopupRoomType {
  name: string
  minRate: number | null
  maxRate: number | null
}

export interface CalendarDayPopupProps {
  date: string
  dayOfWeek: string
  currency: string
  rate?: RateData
  occupancy?: OccupancyData
  propertyOccupancy?: PropertyOccupancyData
  roomType?: PopupRoomType
  adr?: number | null
  lastYearAdr?: number | null
  /** The property's backend-served canonical path — enables the "View target" link. */
  propertyBasePath?: string
  /** Today AT THE PROPERTY (YYYY-MM-DD). Required, not optional: "3 nights away"
   *  is a statement about the hotel's calendar, and an optional prop is how a
   *  surface quietly falls back onto the viewer's clock again. */
  today: string
}

function formatDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })
}

function demandLabel(pct: number): { text: string; color: string } {
  if (pct >= 80) return { text: 'High Demand', color: 'bg-emerald-500' }
  if (pct >= 60) return { text: 'Moderate', color: 'bg-amber-500' }
  return { text: 'Low Demand', color: 'bg-slate-400' }
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="border-t border-border/30 pt-2.5 mt-1 text-[10px] font-bold uppercase tracking-[0.15em] text-primary/50">
      {children}
    </div>
  )
}


export default function CalendarDayPopup({ date, currency, rate, occupancy, propertyOccupancy, roomType, adr, lastYearAdr, propertyBasePath, today }: CalendarDayPopupProps) {
  const sym = currencySymbol(currency)
  // "Building ADR" at a combined inventory (server label), "ADR" everywhere else.
  const { adrLabel } = useFigureScope()
  const adjustments = rate?.computationDetails?.adjustments ?? []
  const isOverride = rate?.source === 'manual_override'
  const leadNights = nightsUntil(date, today)

  // Resolve guardrails — prefer room type, fall back to computation_details
  const minRate = roomType?.minRate ?? rate?.computationDetails?.min_rate ?? null
  const maxRate = roomType?.maxRate ?? rate?.computationDetails?.max_rate ?? null

  // Check if override exceeds bounds
  const overrideAboveCeiling = isOverride && maxRate != null && rate!.rateAmount > maxRate
  const overrideBelowFloor = isOverride && minRate != null && rate!.rateAmount < minRate

  // Check if clamp was applied
  const clampAdj = adjustments.find((a) => a.stage === 'clamp')
  const clampType = clampAdj?.type // 'floor' or 'ceiling'

  // Running totals for lead rooms
  const steps = computeRunningTotals(adjustments, sym)
  const baseStep = steps.find((s) => s.stage === 'base_rate')
  const linkedStep = steps.find((s) => s.stage === 'linked_parent')
  // A Same room (combined inventory): its price is its twin's published price, copied whole. It has
  // no base, no adjustments and no guardrails of its own, so the card shows the copy instead.
  const sameRoomAdj = sameRoomAdjustment(adjustments)
  const sameRoomEngine = sameRoomAdj ? sameRoomEngineRate(sameRoomAdj) : null
  // Its offset from the twin, when it carries one: the step after the copy, ending on its own price.
  const sameRoomOffsetStep = steps.find((s) => s.stage === 'same_room_offset')
  const adjustmentSteps = steps.filter(
    (s) => !['base_rate', 'linked_parent', 'clamp', 'rounding', 'override', 'restrictions', 'same_room', 'same_room_offset'].includes(s.stage)
  )
  const roundingStep = steps.find((s) => s.stage === 'rounding')
  const overrideStep = steps.find((s) => s.stage === 'override')
  // Non-null when the override is a percentage rather than a pinned amount.
  const overridePercent = overridePct(adjustments)
  // Last-Minute Clearance is running on this day (plan §7). The SENTENCE is the backend's own —
  // "Clearance −10%: 2 of 14 rooms unsold, about 3 hours of selling time left" — printed verbatim.
  // The Adjustments row above already carries the money; this block carries the reason, and
  // composing it here would be a second, driftable spelling of a stage's own words.
  const clearanceAdj = adjustments.find((a) => a.stage === 'clearance')

  // Computed rate = last step the engine itself produced, before override/stability/restrictions
  const computedRate = engineComputedTotal(steps)

  // Base rate for above/below comparison
  const baseRate = baseStep?.runningTotal ?? null
  const isAboveBase = baseRate != null && rate != null && rate.rateAmount > baseRate
  const isBelowBase = baseRate != null && rate != null && rate.rateAmount < baseRate

  return (
    <div className="w-[360px] rounded-xl border border-slate-300 bg-white drop-shadow-lg">
      {/* Header */}
      <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 rounded-t-xl">
        <div className="flex items-center justify-between">
          <p className="text-[13px] font-bold text-foreground">{formatDate(date)}</p>
          <span className="text-[11px] text-muted-foreground tabular-nums">
            {nightsAway(leadNights)}
          </span>
        </div>
        {(occupancy || propertyOccupancy) && (
          // The room's own figures are one unbreakable group and the pooled figure another, so the row
          // never strands a lone percentage: when both do not fit, the pooled group moves down whole.
          <div className="mt-1.5 flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
            {occupancy && (
              <div className="flex items-center gap-2 whitespace-nowrap">
                <div className="flex items-center gap-1.5">
                  <div className={cn('h-2 w-2 rounded-full', demandLabel(occupancy.occupancyPct).color)} />
                  <span className="text-[11px] text-muted-foreground">{demandLabel(occupancy.occupancyPct).text}</span>
                </div>
                <span className="text-[11px] text-muted-foreground">
                  {occupancy.booked}/{occupancy.available} rooms
                </span>
                <span className={cn(
                  'text-[11px] font-semibold tabular-nums',
                  occupancy.occupancyPct >= 80 ? 'text-emerald-600' :
                  occupancy.occupancyPct >= 60 ? 'text-amber-600' : 'text-slate-500'
                )}>
                  {occupancy.occupancyPct}%
                </span>
                {occupancy.pickup7dRooms != null && occupancy.pickup7dRooms !== 0 && (
                  <span className={cn(
                    'text-[11px] font-medium tabular-nums',
                    occupancy.pickup7dRooms > 0 ? 'text-emerald-600' : 'text-rose-600'
                  )}>
                    {formatPickupRooms(occupancy.pickup7dRooms)} this week
                  </span>
                )}
              </div>
            )}
            {propertyOccupancy && (!occupancy || propertyOccupancy.available !== occupancy.available) && (
              <div className="flex items-center gap-1.5 whitespace-nowrap ml-auto">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70">
                  {propertyOccupancy.label ?? 'Hotel'}
                </span>
                <span className={cn(
                  'text-[11px] font-semibold tabular-nums',
                  propertyOccupancy.occupancyPct >= 80 ? 'text-emerald-600' :
                  propertyOccupancy.occupancyPct >= 60 ? 'text-amber-600' : 'text-slate-600'
                )}>
                  {propertyOccupancy.occupancyPct}%
                </span>
                <span className="text-[11px] tabular-nums text-muted-foreground">
                  {propertyOccupancy.booked}/{propertyOccupancy.available}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="px-4 py-3 space-y-1.5">
        {/* Stop sell */}
        {rate?.stopSell && (
          <div className="flex items-center gap-2 rounded-lg bg-rose-50 px-3 py-2">
            <Ban className="h-4 w-4 text-rose-600" />
            <div>
              <p className="text-xs font-bold text-rose-700">Stop Sell Active</p>
              <p className="text-[11px] text-rose-600/80">Not available for sale on this date.</p>
            </div>
          </div>
        )}

        {/* Rate breakdown */}
        {rate && !rate.stopSell && (
          <>
            {/* ADR */}
            {adr != null && (
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">{adrLabel ?? 'ADR'}</span>
                <span className="tabular-nums text-muted-foreground">
                  {formatRateSym(adr, sym)}
                  {lastYearAdr != null && (
                    <span className="text-muted-foreground/60"> · LY: {formatRateSym(lastYearAdr, sym)}</span>
                  )}
                </span>
              </div>
            )}

            {/* BASE */}
            {baseStep && (
              <>
                <SectionHeader>Base</SectionHeader>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">{baseStep.label}</span>
                  <span className="font-semibold text-foreground tabular-nums">
                    {formatRateSym(baseStep.runningTotal, sym)}
                  </span>
                </div>
              </>
            )}

            {/* BASE — linked child: parent anchor + linked offset */}
            {linkedStep && (
              <>
                <SectionHeader>Base</SectionHeader>
                <div className="flex justify-between text-xs">
                  <AdjustmentLabel adj={linkedStep.adjustment}>Parent</AdjustmentLabel>
                  <span className="font-semibold text-foreground tabular-nums">
                    {formatRateSym(linkedStep.adjustment.anchor_rate ?? 0, sym)}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Linked offset</span>
                  <span className="tabular-nums">
                    <span className={cn('font-medium', linkedStep.colorClass)}>
                      {linkedOffsetLabel(linkedStep.adjustment, sym)}
                    </span>
                    <span className="text-muted-foreground ml-2">
                      {formatRateSym(linkedStep.runningTotal, sym)}
                    </span>
                  </span>
                </div>
              </>
            )}

            {/* BASE: Same room, the twin's published price, copied */}
            {sameRoomAdj && (
              <>
                <SectionHeader>Base</SectionHeader>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">{stageLabel('same_room', sameRoomAdj.description)}</span>
                  <span className="font-semibold text-foreground tabular-nums">
                    {formatRateSym(sameRoomAdj.copied_rate ?? rate.rateAmount, sym)}
                  </span>
                </div>
                {sameRoomEngine != null && (
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">{sameRoomEngineLabel(sameRoomAdj.twin_room_type_name)}</span>
                    <span className="tabular-nums text-muted-foreground">{formatRateSym(sameRoomEngine, sym)}</span>
                  </div>
                )}
                {sameRoomOffsetStep && (
                  <div className="flex justify-between text-xs">
                    <AdjustmentLabel adj={sameRoomOffsetStep.adjustment}>{sameRoomOffsetStep.label}</AdjustmentLabel>
                    <span className="tabular-nums">
                      <span className={cn('font-medium', sameRoomOffsetStep.colorClass)}>{sameRoomOffsetStep.effect}</span>
                      <span className="text-muted-foreground ml-2">{formatRateSym(sameRoomOffsetStep.runningTotal, sym)}</span>
                    </span>
                  </div>
                )}
              </>
            )}

            {/* ADJUSTMENTS */}
            {adjustmentSteps.length > 0 && (
              <>
                <SectionHeader>Adjustments</SectionHeader>
                {adjustmentSteps.map((step, i) => {
                  const adj = step.adjustment
                  const isTargetPressure = adj.stage === 'target_pressure'
                  // Derive the integer target month (1–12) for the "View target" link.
                  // The controller expects params[:month].to_i.between?(1, 12) — a YYYY-MM
                  // string would parse to the year and fail the guard. Prefer the backend's
                  // stamped target_month; fall back to parsing the stay date's month.
                  const targetMonth =
                    isTargetPressure && propertyBasePath
                      ? (adj.target_month ?? parseInt(date.substring(5, 7), 10))
                      : null
                  return (
                    <React.Fragment key={`${step.stage}-${i}`}>
                      <div className="flex justify-between text-xs">
                        <AdjustmentLabel adj={adj}>{step.label}</AdjustmentLabel>
                        {adj.stage === 'rate_stability' && adj.computed_rate != null && adj.applied_rate != null ? (
                          <span className="tabular-nums font-medium">
                            <span className="text-muted-foreground">{formatRateSym(adj.computed_rate, sym)}</span>
                            <span className="text-amber-500 mx-1">→</span>
                            <span className="text-foreground">{formatRateSym(adj.applied_rate, sym)}</span>
                          </span>
                        ) : (
                          <span className="tabular-nums">
                            <span className={cn('font-medium', step.colorClass)}>{step.effect}</span>
                            <span className="text-muted-foreground ml-2">{formatRateSym(step.runningTotal, sym)}</span>
                          </span>
                        )}
                      </div>
                      {isTargetPressure && adj.type === 'forecast_gap' && (() => {
                        // HOVER CARD sheds ALL Tier-3 (docs/revenue-vocabulary.md § Plain-language):
                        // no ppt / × multiplier / raw percentages — one plain conclusion, plus the
                        // "View target" link. Raw diagnostics live only in EngineDetails on the modal.
                        const dormantReason: string | null = adj.dormant_reason ?? null
                        const finalPct: number = adj.final_modifier_pct ?? 0
                        const rawPct: number = adj.raw_modifier_pct ?? 0

                        let conclusion: string
                        if (dormantReason) {
                          conclusion = dormantReasonLabel(dormantReason)
                        } else if (adj.dilution_guarded) {
                          // Close-in inelastic demand — the discount was fully withheld.
                          conclusion = 'Discount held back — rooms here sell regardless of price'
                        } else if (finalPct === 0 && rawPct !== 0) {
                          conclusion = 'Not enough history to steer this date yet'
                        } else if (adj.adr_limited) {
                          conclusion = 'Discount held at your ADR floor'
                        } else {
                          // Steering off this date's own day-type gap when decomposed, else the
                          // blended month gap. Positive gap ⇒ ahead of target; negative ⇒ behind.
                          const usesDayType = adj.day_type != null && adj.day_type_gap != null
                          const gap = usesDayType ? adj.day_type_gap : adj.target_gap
                          if (gap == null) return null
                          const subject = adj.day_type === 'weekend' ? 'Weekends' : adj.day_type === 'weekday' ? 'Weekdays' : 'This month'
                          conclusion = gap > 0 ? `${subject} running ahead of target` : `${subject} tracking behind target`
                        }

                        return (
                          <div className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground pl-1 -mt-0.5">
                            <span>{conclusion}</span>
                            {targetMonth && (
                              <Link
                                href={`${propertyBasePath}/targets/${targetMonth}`}
                                className="text-primary hover:underline whitespace-nowrap ml-2"
                              >
                                View target
                              </Link>
                            )}
                          </div>
                        )
                      })()}
                    </React.Fragment>
                  )
                })}
              </>
            )}

            {/* Rounding */}
            {roundingStep && roundingStep.adjustment.original !== roundingStep.adjustment.rounded && (
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Rounding</span>
                <span className="tabular-nums text-muted-foreground">{formatRateSym(roundingStep.runningTotal, sym)}</span>
              </div>
            )}

            {/* Computed subtotal (only if override present) */}
            {isOverride && computedRate != null && (
              <div className="border-t border-border/40 pt-1.5 mt-1.5">
                <div className="flex justify-between text-xs">
                  <span className="font-semibold text-foreground">Computed</span>
                  <span className="font-semibold tabular-nums text-foreground">{formatRateSym(computedRate, sym)}</span>
                </div>
              </div>
            )}

            {/* CLEARANCE — the reason, in the stage's own words */}
            {clearanceAdj && (
              <div className="mt-1 rounded-lg border border-amber-400/50 bg-amber-50 px-3 py-2">
                <p className="text-[11px] font-semibold text-amber-800">{clearanceAdj.description}</p>
                <p className="mt-0.5 text-[10px] text-amber-700/80">
                  Back to the normal price once the rooms are gone, or once the night stops heading for
                  empty rooms.
                </p>
              </div>
            )}

            {/* GUARDRAILS: shown when bounds are available (a Same room has none of its own) */}
            {(minRate != null || maxRate != null) && !sameRoomAdj && (
              <>
                <SectionHeader>Guardrails</SectionHeader>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Floor</span>
                  <span className={cn('tabular-nums', clampType === 'floor' ? 'font-medium text-amber-600' : 'text-muted-foreground')}>
                    {minRate != null ? formatRateSym(minRate, sym) : '—'}
                    {clampType === 'floor' && ' applied'}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Ceiling</span>
                  <span className={cn('tabular-nums', clampType === 'ceiling' ? 'font-medium text-amber-600' : 'text-muted-foreground')}>
                    {maxRate != null ? formatRateSym(maxRate, sym) : '—'}
                    {clampType === 'ceiling' && ' applied'}
                  </span>
                </div>
              </>
            )}

            {/* OVERRIDE — highlighted block */}
            {isOverride && overrideStep && (
              <div className="rounded-lg bg-amber-50 border border-amber-400/50 px-3 py-2 mt-1">
                <div className="flex justify-between text-xs">
                  <span className="flex items-center gap-1.5 font-semibold text-amber-800">
                    Override
                    {overridePercent != null && (
                      <span className="rounded bg-amber-200/70 px-1 text-[10px] font-semibold tabular-nums text-amber-900">
                        {overridePctLabel(overridePercent)}
                      </span>
                    )}
                  </span>
                  <span className="font-bold tabular-nums text-amber-800">
                    {formatRateSym(rate.rateAmount, sym)}
                  </span>
                </div>
                {overridePercent != null && (
                  <p className="mt-0.5 text-[10px] text-amber-700/80">
                    A percentage of this day’s computed price — it moves when the engine does.
                  </p>
                )}
                {overrideStep.adjustment.description &&
                  !/^Manual override[^:]*: no reason$/.test(overrideStep.adjustment.description) && (
                    <p className="text-[11px] text-amber-700/80 mt-0.5 italic">
                      {overrideStep.adjustment.description.replace(/^Manual override[^:]*:\s*/, '')}
                    </p>
                  )}
                {(overrideAboveCeiling || overrideBelowFloor) && (
                  <div className="flex items-center gap-1 mt-1">
                    <AlertTriangle className="h-3 w-3 text-amber-600" />
                    <span className="text-[10px] text-amber-700">
                      {overrideAboveCeiling ? `Above ceiling (${formatRateSym(maxRate!, sym)})` : `Below floor (${formatRateSym(minRate!, sym)})`}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* FINAL */}
            <div className="border-t border-border/40 pt-3 mt-2 flex items-baseline justify-between">
              <span className="text-sm font-bold text-foreground">Final</span>
              <span className={cn(
                'text-lg font-extrabold tabular-nums',
                isOverride ? 'text-amber-700' : isAboveBase ? 'text-emerald-700' : isBelowBase ? 'text-rose-700' : 'text-foreground'
              )}>
                {formatRateSym(rate.rateAmount, sym)}
              </span>
            </div>

            {/* PMS SYNC */}
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">PMS Price</span>
              <div className={cn(
                'flex items-center gap-1.5',
                rate.syncStatus === 'synced' ? 'text-emerald-600' :
                rate.syncStatus === 'pending' ? 'text-amber-500' : 'text-muted-foreground'
              )}>
                <span className={cn(
                  'inline-block h-1.5 w-1.5 rounded-full',
                  rate.syncStatus === 'synced' ? 'bg-emerald-500' :
                  rate.syncStatus === 'pending' ? 'bg-amber-500' : 'bg-slate-300'
                )} />
                <span className="tabular-nums font-medium">
                  {rate.pushedRateAmount != null ? formatRateSym(rate.pushedRateAmount, sym) : '--'}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  {rate.syncStatus === 'synced' ? 'In sync' :
                   rate.syncStatus === 'pending' ? 'Pending push' : 'Never pushed'}
                </span>
              </div>
            </div>

            {/* COMBINED INVENTORY: the same rooms on the other listings, each at its own price that night
                and its offset, in the server's sentence. The day details list every listing. */}
            {rate.alsoSoldOn && rate.alsoSoldOn.length > 0 && (
              <div className="mt-1 space-y-0.5 border-t border-border/40 pt-2">
                {rate.alsoSoldOn.map((line) => (
                  <p key={line} className="flex items-start gap-1.5 text-[11px] leading-snug text-muted-foreground">
                    <Link2 className="mt-px h-3 w-3 shrink-0 text-primary/70" aria-hidden="true" />
                    <span className="tabular-nums">{line}</span>
                  </p>
                ))}
              </div>
            )}
          </>
        )}

        {/* Restrictions */}
        {rate && !rate.stopSell && (rate.closedToArrival || rate.closedToDeparture || (rate.minStayArrival && rate.minStayArrival > 1) || rate.maxStay) && (
          <div className="space-y-1.5">
            <div className="border-t border-border/40 pt-2" />
            <div className="flex items-center gap-1.5">
              <Shield className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70">Restrictions</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {rate.closedToArrival && (
                <span title={ABBREV_TOOLTIPS.CTA} className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-700 cursor-help">
                  <DoorClosed className="h-3 w-3" /> CTA
                </span>
              )}
              {rate.closedToDeparture && (
                <span title={ABBREV_TOOLTIPS.CTD} className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-700 cursor-help">
                  <CalendarX className="h-3 w-3" /> CTD
                </span>
              )}
              {rate.minStayArrival != null && rate.minStayArrival > 1 && (
                <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-1 text-[11px] font-medium text-slate-600">
                  <Moon className="h-3 w-3" /> {minNights(rate.minStayArrival)}
                </span>
              )}
              {rate.maxStay != null && (
                <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-1 text-[11px] font-medium text-slate-600">
                  <Moon className="h-3 w-3" /> {maxNights(rate.maxStay)}
                </span>
              )}
            </div>
          </div>
        )}

        {/* No data state — past dates are structurally empty (rates are only
            computed from today forward), so don't make that read like breakage. */}
        {!rate && !occupancy && (
          <p className="text-xs text-muted-foreground text-center py-2">
            {leadNights < 0
              ? "Past dates aren't priced — rates are managed from today onwards."
              : 'No data available for this date.'}
          </p>
        )}
      </div>
    </div>
  )
}
